import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { HeroImage } from '@/components/site/HeroImage';
import { DuotoneImage } from '@/components/site/DuotoneImage';
import { FAQList } from '@/components/site/FAQ';
import { CTABand } from '@/components/site/CTABand';
import { FaqSchema } from '@/components/site/Seo';
import { SITE, SERVICES, WORK } from '@/data/site';
import { CITIES } from '@/data/cities';
import { WORK_IMAGES } from '@/data/homeImages';
import { PLANS } from '@/data/pricing';

// Short, city-agnostic service verbs; the city name is appended at render.
const SERVICE_ANCHORS = {
  'website-development': 'Website development',
  'web-app-development': 'Web app development',
  'mobile-app-development': 'Mobile app development',
  'custom-software-development': 'Custom software development',
  'ecommerce-website-development': 'Ecommerce website development',
  'ai-solutions': 'AI solutions & chatbots',
};

function CityHero({ city }) {
  return (
    <section className="relative overflow-hidden">
      <MouseGlow intensity={0.3} />
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-20 md:pb-28">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ink-muted">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-white">{city.name}</span>
        </nav>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-x-10 md:gap-y-16">
          <div className="md:col-span-9">
            <SectionLabel as="h1" num="—" data-testid={`city-${city.slug}-h1`} className="mt-2">
              {city.h1}
            </SectionLabel>
            <WordReveal
              as="p"
              text={city.headline}
              accent={city.accent}
              className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[78px] font-extrabold tracking-tightest leading-[1.02] text-balance"
            />
          </div>
          <div className="md:col-span-7 md:col-start-6 md:row-start-2">
            <Reveal delay={400}>
              <p className="text-lg md:text-xl text-ink-muted leading-relaxed max-w-xl">{city.intro}</p>
            </Reveal>
            <Reveal delay={520}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <MagneticButton href={SITE.whatsapp} external variant="primary">
                  Book discovery call <ArrowUpRight size={16} />
                </MagneticButton>
                <MagneticButton to="/work/" variant="ghost">See our work</MagneticButton>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:col-start-1 md:row-start-2 self-end">
            <HeroImage src={city.image.src} alt={city.image.alt} testId={`city-${city.slug}-hero-image`} />
          </div>
        </div>
      </div>
    </section>
  );
}

const COST_TIERS = PLANS.filter((p) => ['landing-page', 'business-website', 'ecommerce', 'mobile-app'].includes(p.slug));

const STACK = ['React & Next.js', 'Shopify & headless commerce', 'Flutter & React Native', 'Node.js & Python APIs', 'PostgreSQL & MongoDB', 'OpenAI / Claude AI integrations'];

function CityStory({ city }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num="01">{city.name} businesses</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
            Web development for how {city.name} <span className="italic text-violet">actually works.</span>
          </h2>
        </div>
        <div className="md:col-span-8 space-y-6">
          {city.story.map((para, i) => (
            <Reveal key={i} delay={i * 80}>
              <p className="text-lg text-ink-muted leading-relaxed">{para}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CityDesign({ city }) {
  if (!city.design) return null;
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num="01">Design</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">{city.design.heading}</h2>
        </div>
        <div className="md:col-span-8">
          <Reveal>
            <p className="text-lg text-ink-muted leading-relaxed">{city.design.body}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CityCost({ city }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num="06">Cost &amp; timelines</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
          Website development cost in {city.name}
        </h2>
        <p className="mt-6 text-lg text-ink-muted leading-relaxed max-w-3xl">{city.costIntro}</p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-5">
          {COST_TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 60}>
              <div className="h-full rounded-[16px] border border-white/[0.12] bg-ink-surface p-6">
                <h3 className="text-lg font-semibold tracking-tight">{t.name}</h3>
                <div className="mt-2 text-sm text-ink-muted">from</div>
                <div className="text-2xl font-bold text-violet tabular-nums">{t.from}</div>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{t.timeline} · {t.bestFor}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-ink-muted">
          Prices exclude GST.{' '}
          <Link to="/website-development-cost/" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white">
            Compare full pricing and market rates
          </Link>
          .
        </p>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <h3 className="text-2xl font-bold tracking-tight">Technologies we use</h3>
            <p className="mt-4 text-ink-muted leading-relaxed">{city.stackNote}</p>
          </div>
          <div className="md:col-span-7">
            <Chips items={STACK} />
          </div>
        </div>
      </div>
    </section>
  );
}

function CityServices({ city }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num="02">What we build in {city.name}</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
          Six services, one team, <span className="italic text-electric">for {city.name} businesses.</span>
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 60}>
              <Link
                to={`/services/${s.slug}/`}
                className="group block h-full rounded-[16px] border border-white/[0.12] bg-ink-surface p-6 transition-all duration-500 hover:-translate-y-1 hover:border-white/25"
              >
                <div className="text-xs text-violet tabular-nums">{s.num}</div>
                <h3 className="mt-3 text-xl font-semibold tracking-tight">
                  {SERVICE_ANCHORS[s.slug]} in {city.name}
                </h3>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{s.short}</p>
                <span className="arrow-link mt-5 inline-flex items-center gap-2 text-sm text-white/85">
                  Learn more <span className="arrow">→</span>
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CityWhy({ city }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num="03">Why NioDelta in {city.name}</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
            Local context, <span className="italic text-violet">studio craft.</span>
          </h2>
        </div>
        <ul className="md:col-span-8 divide-y divide-white/10 border-y border-white/10">
          {city.whyLocal.map((w, i) => (
            <li key={w.k} className="py-7 grid grid-cols-1 md:grid-cols-12 gap-6">
              <Reveal className="md:col-span-4" delay={i * 60}>
                <h3 className="text-xl font-semibold tracking-tight">{w.k}</h3>
              </Reveal>
              <Reveal className="md:col-span-8" delay={i * 60 + 70}>
                <p className="text-ink-muted leading-relaxed">{w.v}</p>
              </Reveal>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Chips({ items }) {
  return (
    <div className="mt-6 flex flex-wrap gap-2">
      {items.map((x) => (
        <span key={x} className="text-sm px-3.5 py-1.5 rounded-full border border-white/[0.12] text-white/85">{x}</span>
      ))}
    </div>
  );
}

function CityReach({ city }) {
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-14">
        <div>
          <SectionLabel num="04">Industries</SectionLabel>
          <h2 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">Who we build for in {city.name}</h2>
          <Chips items={city.industries} />
        </div>
        <div>
          <SectionLabel num="04">Areas</SectionLabel>
          <h2 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">Areas we serve around {city.name}</h2>
          <Chips items={city.areas} />
        </div>
      </div>
    </section>
  );
}

function CityWork({ city }) {
  const cases = WORK.filter((w) => city.work.includes(w.slug));
  if (!cases.length) return null;
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel num="05">Relevant work</SectionLabel>
        <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight">Projects like yours</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {cases.map((w) => (
            <Link key={w.slug} to={`/work/${w.slug}/`} className="group block rounded-[20px] border border-white/[0.12] overflow-hidden hover:border-white/25 transition-colors">
              <div className="relative aspect-[16/9]">
                {WORK_IMAGES[w.slug] && <DuotoneImage src={WORK_IMAGES[w.slug].src} alt={WORK_IMAGES[w.slug].alt} duotone={0.35} />}
                <div className="absolute bottom-4 left-5 text-4xl font-bold tabular-nums text-white">{w.stat.kpi}</div>
              </div>
              <div className="p-6">
                <div className="text-[11px] uppercase tracking-[0.24em] text-ink-muted">{w.sector}</div>
                <h3 className="mt-2 text-2xl font-semibold tracking-tight">{w.name}</h3>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{w.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CityFaq({ city }) {
  const others = CITIES.filter((c) => c.slug !== city.slug);
  return (
    <section className="relative py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <SectionLabel num="07">FAQ</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
            Questions from {city.name} clients
          </h2>
          <div className="mt-8 text-sm text-ink-muted">
            Also serving{' '}
            {others.map((c, i) => (
              <React.Fragment key={c.slug}>
                {i > 0 && (i === others.length - 1 ? ' and ' : ', ')}
                <Link to={`${c.path}/`} className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white">
                  {c.name}
                </Link>
              </React.Fragment>
            ))}
            .
          </div>
          {city.related && (
            <ul className="mt-4 space-y-2 text-sm">
              {city.related.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="md:col-span-8">
          <FAQList items={city.faqs} testIdPrefix={`city-${city.slug}-faq`} />
        </div>
      </div>
    </section>
  );
}

export default function City({ slug }) {
  const city = CITIES.find((c) => c.slug === slug);
  return (
    <div data-testid={`city-${slug}-page`}>
      <FaqSchema items={city.faqs} />
      <CityHero city={city} />
      <CityStory city={city} />
      <CityDesign city={city} />
      <CityServices city={city} />
      <CityWhy city={city} />
      <CityReach city={city} />
      <CityWork city={city} />
      <CityCost city={city} />
      <CityFaq city={city} />
      <CTABand
        eyebrow={`Based near ${city.name}?`}
        headline={`Let’s talk about your ${city.name} project.`}
        sub="Discovery calls are 30 minutes, free, and on WhatsApp. For bigger scopes we’ll come to you."
      />
    </div>
  );
}
